import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SendIcon from '@mui/icons-material/Send';
import { useMutation } from '@apollo/client';
import { PlanStep, StepStatus, StepIntent, StepKind } from '../types/generated/graphql';
import { colors } from '../theme/colors';
import StepIntakePanel from './StepIntakePanel';
import StepRequestThreadPanel from './StepRequestThreadPanel';
import DynamicRequestForm from './request-thread/DynamicRequestForm';
import { DescriptionChecklist } from './shared/DescriptionChecklist';
import { DoThisForMeButton } from './shared/DoThisForMeButton';
import { canExecuteStep } from '../utils/planHelpers';
import { generateFieldsFromStep } from '../utils/step-field-generator';
import { SUBMIT_STEP_INTAKE_INLINE, GET_PLAN } from '../graphql/queries';
import { AsyncConnectorActivityCard } from './plan-tabs/tasks-pane/AsyncConnectorActivityCard';

interface StepDetailDialogProps {
  open: boolean;
  step: PlanStep | null;
  planId: string;
  onClose: () => void;
  onToggleCompletion?: (stepId: string, completed: boolean) => void;
}

const getStatusInfo = (status: StepStatus) => {
  switch (status) {
    case StepStatus.Completed:
      return { label: 'Completed', color: colors.green[600], icon: CheckCircleIcon, bgcolor: colors.green[50] };
    case StepStatus.InProgress:
      return { label: 'In Progress', color: colors.blue[600], icon: PendingIcon, bgcolor: colors.blue[50] };
    case StepStatus.WaitingExternal:
      return { label: 'Waiting for response', color: colors.amber[600], icon: PendingIcon, bgcolor: colors.amber[50] };
    case StepStatus.Blocked:
      return { label: 'Blocked', color: colors.red[600], icon: BlockIcon, bgcolor: colors.red[50] };
    default:
      return { label: 'Not Started', color: colors.gray[500], icon: PendingIcon, bgcolor: colors.gray[100] };
  }
};

// Determine if step should show data entry panel
const shouldShowDataEntry = (step: PlanStep): boolean => {
  // Always show for explicit collection intents
  if (step.intent === StepIntent.CollectDocument || step.intent === StepIntent.DataEntry) {
    return true;
  }
  // Show if intake already exists
  if (step.intake) {
    return true;
  }
  // Show for HUMAN steps - they need a place to capture outcomes/notes
  if (step.kind === StepKind.Human) {
    return true;
  }
  return false;
};

// Determine if this is an explicit data collection step vs outcome capture
const isExplicitCollectionIntent = (intent: StepIntent | null | undefined): boolean => {
  return intent === StepIntent.CollectDocument || intent === StepIntent.DataEntry;
};

export const StepDetailDialog: React.FC<StepDetailDialogProps> = ({
  open,
  step,
  planId,
  onClose,
  onToggleCompletion,
}) => {
  // Memoize canExecuteStep result to avoid multiple calls during render
  // Must be called before any early returns to follow Rules of Hooks
  const canExecute = useMemo(() => step ? canExecuteStep(step) : false, [step]);

  // Structured form from step.allowedFields (same as Tasks tab) when opening from Overview
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const generatedFields = useMemo(() => {
    if (!step) return { fields: [] as Array<{ key: string; label: string; type: string; required: boolean; description?: string; enumValues?: string[] }>, uiMode: 'auto' as const };
    const allowed = step.allowedFields;
    if (allowed && allowed.length > 0) {
      return {
        fields: allowed.map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          required: f.required,
          description: f.description ?? undefined,
          enumValues: f.enumValues ?? undefined,
        })),
        uiMode: 'form' as const,
      };
    }
    const result = generateFieldsFromStep(step.title, step.description);
    return { fields: result.fields, uiMode: result.uiMode };
  }, [step]);

  const isLockedByConnector =
    step != null &&
    (step.status === StepStatus.WaitingExternal ||
      (step.status === StepStatus.Blocked &&
        (step.context as { connectorWait?: unknown })?.connectorWait != null));
  const showStructuredForm =
    step &&
    generatedFields.uiMode === 'form' &&
    generatedFields.fields.length > 0 &&
    !step.intake &&
    !isLockedByConnector;
  const [submitInline, { loading: submittingInline, error: submitInlineError }] = useMutation(SUBMIT_STEP_INTAKE_INLINE, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    onCompleted: () => {
      setFormValues({});
      onClose();
    },
  });

  const handleFieldChange = (key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };
  const handleStructuredSubmit = async () => {
    if (!step || Object.keys(formValues).length === 0) return;
    await submitInline({
      variables: {
        planId,
        stepId: step.id,
        input: { structuredData: formValues, rawText: null, fileIds: [] },
      },
    });
  };
  const hasStructuredFormData = Object.keys(formValues).some((k) => {
    const v = formValues[k];
    return v !== undefined && v !== null && v !== '';
  });

  if (!step) return null;

  const statusInfo = getStatusInfo(step.status);
  const StatusIcon = statusInfo.icon;
  const showDataEntry = shouldShowDataEntry(step) && !isLockedByConnector;
  const isExplicitCollection = isExplicitCollectionIntent(step.intent);
  const isCompleted = step.status === StepStatus.Completed;

  const handleToggleCompletion = () => {
    onToggleCompletion?.(step.id, !isCompleted);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          pr: 6,
          pb: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {step.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<StatusIcon sx={{ fontSize: 14 }} />}
              label={statusInfo.label}
              size="small"
              sx={{
                bgcolor: statusInfo.bgcolor,
                color: statusInfo.color,
                fontWeight: 500,
                '& .MuiChip-icon': { color: statusInfo.color },
              }}
            />
            {step.intent && (
              <Chip
                label={step.intent.replace(/_/g, ' ')}
                size="small"
                sx={{
                  bgcolor: colors.gray[100],
                  color: colors.textSecondary,
                  fontWeight: 500,
                }}
              />
            )}
            {step.kind && (
              <Chip
                label={step.kind}
                size="small"
                sx={{
                  bgcolor: colors.gray[100],
                  color: colors.textSecondary,
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Description - with checklist support for lists */}
        {step.description && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              Description
            </Typography>
            <DescriptionChecklist
              stepId={step.id}
              description={step.description}
              isCompleted={isCompleted}
            />
          </Box>
        )}

        {/* Context/Notes */}
        {step.context?.notes && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}
            >
              Notes
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {step.context.notes}
            </Typography>
          </Box>
        )}

        {/* Links */}
        {step.context?.links && step.context.links.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}
            >
              Links
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {step.context.links.map((link, idx) => (
                <Typography
                  key={idx}
                  component="a"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: colors.blue[600],
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* ─── Phase 3.5 Extensions: Owner, Due Date, Acceptance, Artifacts, Deps ─── */}
        <Divider sx={{ my: 2 }} />

        {/* Owner */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <PersonIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              Owner
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {step.assignedRoleId ?? 'Unassigned'}
          </Typography>
        </Box>

        {/* Due Date */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              Due Date
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        </Box>

        {/* Acceptance Criteria */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <ChecklistIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              Acceptance Criteria
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            No acceptance criteria defined.
          </Typography>
        </Box>

        {/* Required Artifacts */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <AttachFileIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              Required Artifacts
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        </Box>

        {/* Dependencies */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <AccountTreeIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
            <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              Dependencies
            </Typography>
          </Stack>
          {step.dependsOn && step.dependsOn.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {step.dependsOn.map((depId) => (
                <Chip key={depId} label={depId} size="small" variant="outlined" sx={{ mb: 0.5 }} />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No dependencies
            </Typography>
          )}
        </Box>

        {/* Locked state when waiting on async connector or blocked */}
        {isLockedByConnector && step && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {step.status === StepStatus.WaitingExternal
                ? 'This step is waiting for an external response. You cannot resubmit or edit until it completes.'
                : 'This step is blocked. See details below.'}
            </Typography>
            <AsyncConnectorActivityCard step={step} compact={false} />
          </>
        )}

        {/* Structured form in a step tile (e.g. Contact information, Select agency) */}
        {showStructuredForm && step && (
          <>
            <Divider sx={{ my: 3 }} />
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${colors.blue[400]}` }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {step.title}
                </Typography>
                {step.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>
                )}
                <DynamicRequestForm
                  allowedFields={generatedFields.fields}
                  values={formValues}
                  onChange={handleFieldChange}
                  disabled={submittingInline}
                />
                {submitInlineError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {submitInlineError.message}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={submittingInline ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                    onClick={handleStructuredSubmit}
                    disabled={submittingInline || !hasStructuredFormData}
                  >
                    {submittingInline ? 'Submitting...' : 'Submit'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </>
        )}

        {/* Intake Panel - for data collection and human step outcome capture (when no structured form or after completion) */}
        {showDataEntry && !showStructuredForm && (
          <>
            <Divider sx={{ my: 3 }} />
            <StepIntakePanel
              planId={planId}
              step={step}
              onIntakeComplete={onClose}
              mode={isExplicitCollection ? 'collect' : 'outcome'}
            />
          </>
        )}

        {/* Request Thread Panel - for conversational data collection */}
        <Divider sx={{ my: 3 }} />
        <StepRequestThreadPanel
          planId={planId}
          step={step}
          onThreadCreated={onClose}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>

        {/* Delegate button (Phase 3.7) */}
        <Button size="small" sx={{ color: colors.textSecondary }}>
          Delegate
        </Button>

        {/* For executable automation/mixed steps: Show "Do This for Me" as primary */}
        {canExecute && (
          <>
            <Button
              size="small"
              onClick={handleToggleCompletion}
              sx={{ color: colors.textSecondary }}
            >
              {isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
            </Button>
            <DoThisForMeButton
              planId={planId}
              step={step}
              variant="primary"
              onSuccess={onClose}
              canExecute={canExecute}
            />
          </>
        )}
        
        {/* For non-executable steps (HUMAN or no actions): Show completion button as primary */}
        {!canExecute && !showDataEntry && (
          <Button
            variant="contained"
            onClick={handleToggleCompletion}
            color={isCompleted ? 'inherit' : 'success'}
          >
            {isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StepDetailDialog;
